import { Badge } from '@latitude/badge';
import { cn } from '@latitude/token-utils';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import type { ComponentRef, ReactNode, Ref } from 'react';
import { triggerVariants } from 'variants/trigger-variants';

import { useAccordionContext } from '../AccordionContext';
import type {
  AccordionTriggerBadgeProps,
  AccordionTriggerProps,
  AccordionTriggerSuffixProps,
  AccordionTriggerTextProps,
} from '../types';
import { AccordionBaseTrigger } from './AccordionBaseTrigger';

/**
 * Renders an accordion trigger icon.
 *
 * @component
 * @param {ReactNode} icon - The icon to be rendered.
 * @returns {JSX.Element} - The rendered accordion trigger icon.
 */
const AccordionTriggerIcon = ({ icon }: { icon: ReactNode }) => (
  <span
    className={cn([
      '[&_>svg]:size-sizing-sys-size-24',
      // Default color/fill for icons that don't set their own
      'fill-current',
      'text-current',
      'size-[24px]',
      'flex items-center justify-center',
      'pointer-events-none',
    ])}
  >
    {icon}
  </span>
);

/**
 * Renders a badge component for the accordion.
 *
 * @param {AccordionTriggerBadgeProps} badgeProps - The props for the AccordionTriggerBadge component.
 * @returns {JSX.Element} The rendered AccordionTriggerBadge component.
 */
const AccordionTriggerBadge = (badgeProps: AccordionTriggerBadgeProps) => (
  <Badge {...badgeProps} className="shrink-0">
    {badgeProps.text}
  </Badge>
);

/**
 * Renders a suffix for the AccordionTrigger component.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {React.ReactNode} props.suffix - The suffix text to be rendered.
 * @returns {JSX.Element} - The rendered suffix element.
 */
const AccordionTriggerSuffix = ({ suffix }: AccordionTriggerSuffixProps) => (
  <span
    className={cn([
      'text-color-comp-accordion-filled-subtext-default',
      'font-comp-accordion-label-font-family',
      'font-comp-accordion-label-font-weight',
      'text-font-comp-accordion-label-font-size',
      'font-comp-accordion-label-font-weight',
      'leading-font-comp-accordion-label-line-height',
      'group-[&[data-disabled]]:text-inherit',
    ])}
  >
    {suffix}
  </span>
);

/**
 * Renders the text content for an accordion item.
 *
 * @component
 * @param {AccordionTriggerTextProps} props - The props for the AccordionTriggerText component.
 * @param {string} props.title - The title of the accordion item.
 * @param {string} props.subtitle - The subtitle of the accordion item.
 * @returns {JSX.Element} The rendered AccordionTriggerText component.
 */
const AccordionTriggerText = ({
  title,
  subtitle,
}: AccordionTriggerTextProps) => {
  return (
    <div className="gap-spacing-comp-accordion-small-gap pointer-events-none flex grow flex-col items-start">
      <span data-testid="trigger-button-title">{title}</span>
      {subtitle && (
        <span
          data-subtitle
          data-testid="trigger-button-subtitle"
          className="font-comp-accordion-sub-text-font-family font-comp-accordion-sub-text-font-weight text-font-comp-accordion-sub-text-font-size leading-font-comp-accordion-sub-text-line-height duration-300 group-disabled:text-inherit"
        >
          {subtitle}
        </span>
      )}
    </div>
  );
};

/**
 * Represents the trigger component for an accordion item.
 *
 * @component
 * @example
 * ```tsx
 * <AccordionTrigger title="Accordion Title" subtitle="Accordion Subtitle" />
 * ```
 */
export const AccordionTrigger = ({
  className,
  title,
  subtitle,
  icon,
  badgeProps,
  suffix,
  ref,
  ...props
}: AccordionTriggerProps & {
  ref?: Ref<ComponentRef<typeof AccordionPrimitive.Trigger>>;
}) => {
  const context = useAccordionContext();
  return (
    <AccordionBaseTrigger
      ref={ref}
      className={cn(triggerVariants({ size: context?.size }), className)}
      data-testid="accordion-trigger"
      {...props}
    >
      {icon && <AccordionTriggerIcon icon={icon} />}
      {<AccordionTriggerText title={title} subtitle={subtitle} />}

      {suffix && <AccordionTriggerSuffix suffix={suffix} />}
      {badgeProps && <AccordionTriggerBadge {...badgeProps} />}
    </AccordionBaseTrigger>
  );
};

AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;
